import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download, Loader2, RotateCcw, Trash2, Plus, ListChecks, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { PROBLEM_STATUS_META } from '../mock/data';
import { PageHeader, StatCard, StatusBadge } from '../components/common';
import { useI18n } from '../i18n/I18nContext';
import { useRealtime } from '../realtime/RealtimeContext';
import { exportCSV } from '../utils/exportCSV';
import { formatDateTime } from '../utils/prefs';
import api from '../api/client';

const SEV = { HIGH: { c: 'var(--kmt-red)' }, MEDIUM: { c: 'var(--kmt-amber)' }, LOW: { c: 'var(--kmt-blue)' } };
const PROBLEM_TYPES = ['Mechanical', 'Electrical', 'Calibration', 'Tooling', 'Material', 'Software', 'Safety', 'Other'];
const COMMON_SET = [
  { en: 'Motor overheating', type: 'Electrical', severity: 'HIGH' },
  { en: 'Coolant leak', type: 'Mechanical', severity: 'MEDIUM' },
  { en: 'Sensor not detecting part', type: 'Electrical', severity: 'MEDIUM' },
  { en: 'Drive belt slipping', type: 'Mechanical', severity: 'MEDIUM' },
  { en: 'Chuck jaw worn', type: 'Tooling', severity: 'LOW' },
  { en: 'Program not loading', type: 'Software', severity: 'MEDIUM' },
  { en: 'Air pressure low', type: 'Mechanical', severity: 'MEDIUM' },
];

export default function ProblemsDowntime() {
  const { t, lang } = useI18n();
  const langKey = (lang || 'EN').toLowerCase();
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('ALL');

  // quick-report catalog management
  const [catalog, setCatalog] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [newEn, setNewEn] = useState('');
  const [newType, setNewType] = useState('Mechanical');
  const [newSev, setNewSev] = useState('MEDIUM');
  const [saving, setSaving] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = () => api.listProblems().then(setProblems).catch(() => {}).finally(() => setLoading(false));
  const loadCatalog = () => api.listProblemPresets().then((d) => setCatalog(d.catalog || [])).catch(() => {});
  useEffect(() => { load(); loadCatalog(); }, []);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
  useRealtime(() => { load(); loadCatalog(); });

  const descOf = (p) => (p.translations && p.translations[langKey]) || p.desc;
  const tType = (tp) => { const v = t(`ptype.${tp}`); return v === `ptype.${tp}` ? tp : v; };
  const tSev = (s) => { const v = t(`sev.${s}`); return v === `sev.${s}` ? s : v; };

  // Live downtime: ticks for open tickets, frozen once the machine restarts / ticket resolves
  const liveDowntime = (p) => {
    if (p.downtime_closed || p.status === 'RESOLVED' || !p.ts) return p.downtime;
    const start = new Date(p.ts).getTime();
    if (Number.isNaN(start)) return p.downtime;
    const mins = Math.max(0, Math.floor((now - start) / 60000));
    return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
  };
  const isLive = (p) => !p.downtime_closed && p.status !== 'RESOLVED' && !!p.ts;

  const addPreset = async () => {
    if (!newEn.trim()) { toast.error(t('problems.enterLabel')); return; }
    setSaving(true);
    try {
      await api.createProblemPreset({ en: newEn.trim(), type: newType, severity: newSev });
      toast.success(t('problems.reasonAdded'));
      setNewEn(''); loadCatalog();
    } catch (e) { toast.error(t('common.failed')); }
    finally { setSaving(false); }
  };

  const removePreset = async (id) => {
    setCatalog((prev) => prev.filter((c) => c.id !== id));
    try { await api.deleteProblemPreset(id); } catch (e) { toast.error(t('common.deleteFailed')); loadCatalog(); }
  };

  const bulkAdd = async (items) => {
    if (!items.length) { toast.error(t('problems.nothingAdd')); return; }
    setBulkBusy(true);
    try {
      const r = await api.bulkCreatePresets(items);
      toast.success(`${r.created} ${t('problems.bulkAdded')}`);
      setBulkText(''); loadCatalog();
    } catch (e) { toast.error(t('problems.bulkFailed')); }
    finally { setBulkBusy(false); }
  };
  const bulkAddText = () => bulkAdd(
    bulkText.split('\n').map((l) => l.trim()).filter(Boolean).map((en) => ({ en, type: newType, severity: newSev }))
  );

  const open = problems.filter((p) => p.status === 'OPEN').length;
  const inprog = problems.filter((p) => p.status === 'IN_PROGRESS').length;
  const resolved = problems.filter((p) => p.status === 'RESOLVED').length;
  const rows = problems.filter((p) => tab === 'ALL' || p.status === tab);

  const parseDt = (s) => {
    const h = /(\d+)\s*h/.exec(s || '');
    const m = /(\d+)\s*m/.exec(s || '');
    return (h ? +h[1] : 0) + (m ? +m[1] : 0) / 60;
  };
  const totalDowntime = problems.reduce((s, p) => s + parseDt(liveDowntime(p)), 0);

  const resolve = async (p) => {
    setProblems((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'RESOLVED' } : x)));
    try {
      await api.updateProblem(p.id, 'RESOLVED');
      toast.success(`${p.id} ${t('common.resolve').toLowerCase()}d`);
    } catch (e) { toast.error(t('common.failed')); load(); }
  };

  const handleExport = () => {
    exportCSV('kmt_problems_downtime.csv', [
      { label: 'Ticket', get: (r) => r.id },
      { label: 'Machine', get: (r) => r.machine },
      { label: 'Name', get: (r) => r.name },
      { label: 'Type', get: (r) => r.type },
      { label: 'Severity', get: (r) => r.severity },
      { label: 'Downtime', get: (r) => liveDowntime(r) },
      { label: 'Reported', get: (r) => (r.ts ? formatDateTime(r.ts) : r.reported) },
      { label: 'Reported By', get: (r) => r.by },
      { label: 'Status', get: (r) => r.status },
      { label: 'Description', get: (r) => descOf(r) },
    ], problems);
    toast.success(t('problems.exported'));
  };

  const removeProblem = async (p) => {
    setProblems((prev) => prev.filter((x) => x.id !== p.id));
    try { await api.deleteProblem(p.id); toast.success(`${p.id} ${t('problems.deleted')}`); }
    catch (e) { toast.error(t('common.deleteFailed')); load(); }
  };

  const resetAll = async () => {
    if (!window.confirm(t('problems.resetConfirm'))) return;
    try {
      const res = await api.resetProblems();
      setProblems([]);
      if (res.undo_token) {
        toast.success(t('problems.allReset'), {
          duration: 60000,
          action: {
            label: t('common.undo'),
            onClick: async () => {
              try { await api.restoreProblems(res.undo_token); toast.success(t('problems.ticketsRestored')); load(); }
              catch (e) { toast.error(e?.response?.data?.detail || t('common.undoFailed')); }
            },
          },
        });
      } else {
        toast.success(t('problems.noReset'));
      }
    } catch (e) { toast.error(t('common.resetFailed')); load(); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={AlertTriangle} title={t('problems.title')} subtitle={`${problems.length} ${t('problems.sub')}`}
        right={<>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={handleExport} data-testid="problems-export-csv-btn"><Download size={16} />{t('problems.exportCsv')}</button>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={resetAll} data-testid="problems-reset-all-btn" style={{ color: 'var(--kmt-red)' }}><RotateCcw size={15} />{t('common.resetAll')}</button>
        </>} />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label={t('problems.open')} value={open} accent="var(--kmt-red)" />
          <StatCard label={t('problems.inProgress')} value={inprog} accent="var(--kmt-blue)" />
          <StatCard label={t('problems.resolved')} value={resolved} accent="var(--kmt-green)" />
          <StatCard label={t('problems.totalDowntime')} value={`${totalDowntime.toFixed(1)}h`} accent="var(--kmt-amber)" />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {['ALL', 'OPEN', 'IN_PROGRESS', 'RESOLVED'].map((tt) => (
            <button key={tt} onClick={() => setTab(tt)} className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: tab === tt ? 'var(--kmt-gold)' : 'var(--kmt-panel-2)', color: tab === tt ? '#17140a' : 'var(--kmt-muted)', border: '1px solid var(--kmt-border)' }}>
              {t(`pstatus.${tt}`)}
            </button>
          ))}
          <button onClick={() => setShowCatalog((s) => !s)} data-testid="toggle-catalog-btn"
            className="ml-auto px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            style={{ background: 'var(--kmt-panel-2)', color: 'var(--kmt-gold)', border: '1px solid var(--kmt-border)' }}>
            <ListChecks size={15} />{t('problem.reasonsBtn')} ({catalog.length})
          </button>
        </div>

        {showCatalog && (
          <div className="kmt-panel p-6 space-y-4" data-testid="catalog-panel">
            <div className="kmt-label">{t('problem.reasonsHint')}</div>
            <div className="flex flex-col md:flex-row gap-3">
              <input className="kmt-input flex-1" placeholder={t('problem.addLabel')} value={newEn} onChange={(e) => setNewEn(e.target.value)} data-testid="catalog-en-input" />
              <select className="kmt-input md:w-44" value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="catalog-type-input">
                {PROBLEM_TYPES.map((tp) => <option key={tp} value={tp}>{tType(tp)}</option>)}
              </select>
              <select className="kmt-input md:w-36" value={newSev} onChange={(e) => setNewSev(e.target.value)} data-testid="catalog-sev-input">
                {['HIGH', 'MEDIUM', 'LOW'].map((s) => <option key={s} value={s}>{tSev(s)}</option>)}
              </select>
              <button className="kmt-btn-gold flex items-center gap-2" disabled={saving} onClick={addPreset} data-testid="catalog-add-btn">
                {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={16} />}{t('common.add')}
              </button>
            </div>
            <div className="pt-3" style={{ borderTop: '1px solid var(--kmt-border-soft)' }}>
              <div className="kmt-label mb-2">{t('problems.bulkHint')}</div>
              <textarea className="kmt-input" rows={3} placeholder={'Motor overheating\nCoolant leak\nSensor fault'} value={bulkText} onChange={(e) => setBulkText(e.target.value)} data-testid="catalog-bulk-input" />
              <div className="flex gap-2 mt-2 flex-wrap">
                <button className="kmt-btn-ghost flex items-center gap-2" disabled={bulkBusy} onClick={bulkAddText} data-testid="catalog-bulk-add-btn">
                  {bulkBusy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}{t('common.bulkAdd')}
                </button>
                <button className="kmt-btn-ghost flex items-center gap-2" disabled={bulkBusy} onClick={() => bulkAdd(COMMON_SET)} data-testid="catalog-common-set-btn">
                  <Plus size={14} />{t('problems.addCommonSet')} ({COMMON_SET.length})
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {catalog.length === 0 && <span className="text-sm" style={{ color: 'var(--kmt-muted)' }}>{t('problems.noReasons')}</span>}
              {catalog.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid={`catalog-item-${c.id}`}>
                  <span style={{ color: (SEV[c.severity] || SEV.MEDIUM).c, fontWeight: 700, fontSize: 10 }}>{tSev(c.severity)}</span>
                  <span className="text-sm">{c[langKey] || c.en}</span>
                  <span className="text-xs" style={{ color: 'var(--kmt-muted-2)' }}>· {tType(c.type)}</span>
                  <button onClick={() => removePreset(c.id)} data-testid={`catalog-delete-${c.id}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">{t('col.ticket')}</th><th className="kmt-th">{t('col.machine')}</th><th className="kmt-th">{t('col.type')}</th><th className="kmt-th">{t('col.severity')}</th><th className="kmt-th">{t('col.downtime')}</th><th className="kmt-th">{t('col.reported')}</th><th className="kmt-th">{t('col.status')}</th><th className="kmt-th">{t('col.action')}</th>
              </tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} data-testid={`problem-row-${p.id}`}>
                    <td className="kmt-td font-mono-k" data-label="Ticket" style={{ color: 'var(--kmt-gold)' }}>{p.id}</td>
                    <td className="kmt-td" data-label="Machine"><span className="font-mono-k">{p.machine}</span> <span style={{ color: 'var(--kmt-muted)' }}>{p.name}</span><div className="text-xs" style={{ color: 'var(--kmt-muted-2)' }}>{descOf(p)}</div></td>
                    <td className="kmt-td" data-label="Type">{tType(p.type)}</td>
                    <td className="kmt-td" data-label="Severity"><span style={{ color: (SEV[p.severity] || SEV.MEDIUM).c, fontWeight: 600, fontSize: 12 }}>{tSev(p.severity)}</span></td>
                    <td className="kmt-td font-mono-k" data-label="Downtime">
                      {liveDowntime(p)}
                      {isLive(p) && <span className="ml-2 kmt-dot" style={{ background: 'var(--kmt-red)', display: 'inline-block' }} title={t('problems.liveTitle')} />}
                    </td>
                    <td className="kmt-td" data-label="Reported" style={{ color: 'var(--kmt-muted)' }}>{p.ts ? formatDateTime(p.ts) : p.reported}<div className="text-xs">{t('common.by')} {p.by}</div></td>
                    <td className="kmt-td" data-label="Status"><StatusBadge meta={{ ...(PROBLEM_STATUS_META[p.status] || PROBLEM_STATUS_META.OPEN), label: t(`pstatus.${p.status}`) }} /></td>
                    <td className="kmt-td" data-label="Action">
                      <div className="flex items-center gap-2">
                        {p.status !== 'RESOLVED' && <button className="kmt-btn-ghost text-xs" onClick={() => resolve(p)} data-testid={`problem-resolve-${p.id}`}>{t('common.resolve')}</button>}
                        <button className="kmt-btn-ghost text-xs flex items-center gap-1" onClick={() => removeProblem(p)} data-testid={`problem-delete-${p.id}`} style={{ color: 'var(--kmt-red)' }}><Trash2 size={13} />{t('common.delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
